import { forwardRef, useImperativeHandle, useState } from "react"

type ModalAction = {
    label: string
    variant?: "primary" | "secondary" | "danger"
    autoClose?: boolean
    onClick: () => void | Promise<void>
}

type ModalOptions = {
    title: string
    description?: string
    actions?: ModalAction[]
    onDismiss?: () => void
}

export type ConfirmModalHandle = {
    open: (options: ModalOptions) => void
    close: () => void
}

const buttonVariants = {
    primary: "text-[#4da3ff]",
    secondary: "text-text/70",
    danger: "text-[#ff453a]"
}

const ConfirmModal = forwardRef<ConfirmModalHandle>((_, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<ModalOptions | null>(null)

    useImperativeHandle(ref, () => ({
        open(opts) {
            setOptions(opts)
            setIsOpen(true)
        },
        close() {
            setIsOpen(false)
        }
    }))

    if (!isOpen || !options) return null

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center px-4"
            style={{ background: "rgba(10,18,42,0.75)", backdropFilter: "blur(6px)" }}
            onClick={() => {
                setIsOpen(false)
                options.onDismiss?.()
            }}>
            <div
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm overflow-hidden"
                style={{
                    background: "linear-gradient(160deg, #1e2f58 0%, #1a1438 100%)",
                    border: "1.5px solid rgba(255,255,255,0.12)",
                    borderRadius: "24px",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
                }}>
                <div className="flex flex-col gap-2 px-6 py-5 text-center">
                    <div
                        className="text-[17px] font-bold text-white"
                        style={{ fontFamily: "'Tilda Sans', sans-serif", letterSpacing: "-0.01em" }}>
                        {options.title}
                    </div>
                    {options.description && (
                        <div className="text-[14px] text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {options.description}
                        </div>
                    )}
                </div>

                <div className="border-t border-white/10 pb-[max(0px,var(--safe-bottom))]">
                    <div className="flex flex-col divide-y divide-white/[0.08]">
                        {options.actions?.map((action, index) => {
                            const handleClick = () => {
                                if (action.autoClose !== false) setIsOpen(false)
                                Promise.resolve(action.onClick()).catch(console.error)
                            }
                            return (
                                <button
                                    key={index}
                                    onClick={handleClick}
                                    className={`cursor-pointer py-3.5 text-[14px] font-bold transition hover:bg-white/[0.06] ${buttonVariants[action.variant ?? "secondary"]}`}
                                    style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                    {action.label}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
})

export default ConfirmModal
